"use client";

import Link from "next/link";
import { Fragment, useMemo, useRef, useState } from "react";

import TournamentFilters from "./TournamentFilters";
import {tournamentMatchesFilters,tournamentYears,tournamentType} from "@/lib/tournament-filters";
import CupBracket from "./CupBracket";
import type {CupData} from "@/lib/cup";
import {tournamentTitle} from "@/lib/tournament-title";
import TeamCrest from "@/components/TeamCrest";
import TeamKit from "@/components/TeamKit";
import type { ClubProfileData } from "@/lib/clubs";

type TableRowLite = {
  teamId: number;
  team: string;
  position: number;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDiff: number;
  points: number;
};

export default function ClubHistory({
  profile,
  admin = false,
}: {
  profile: ClubProfileData;
  admin?: boolean;
}) {
  const { club, seasons, games } = profile;
  const [ageCategory,setAgeCategory]=useState("all");
  const [year,setYear]=useState("all");
  const [type,setType]=useState("all");
  const tableRequest=useRef(0);
  const filteredSeasons=useMemo(()=>seasons.filter(s=>tournamentMatchesFilters({...s,format:s.name},{ageCategory,year,type,season:"all"})),[seasons,ageCategory,year,type]);
  const categoryGames=useMemo(()=>{const slugs=new Set(filteredSeasons.map(s=>s.slug));return games.filter(g=>slugs.has(g.tournamentSlug));},[games,filteredSeasons]);
  const totals = useMemo(() => {
    const value = { played: 0, won: 0, drawn: 0, lost: 0, goalsFor: 0, goalsAgainst: 0, goalDiff: 0, points: 0 };
    for (const game of categoryGames) {
      const season = seasons.find((item) => item.slug === game.tournamentSlug);
      const home = game.home === (season?.teamName ?? club.name);
      const scored = home ? game.homeGoals : game.awayGoals;
      const conceded = home ? game.awayGoals : game.homeGoals;
      value.played += 1; value.goalsFor += scored; value.goalsAgainst += conceded;
      if (scored > conceded) { value.won += 1; value.points += 3; }
      else if (scored < conceded) value.lost += 1;
      else { value.drawn += 1; value.points += 1; }
    }
    value.goalDiff = value.goalsFor - value.goalsAgainst;
    return value;
  }, [categoryGames, seasons, club.name]);

  // Матчи выбранного турнира раскрываются непосредственно под его строкой.
  const [selectedTournamentId, setSelectedTournamentId] = useState<number | null>(null);

  const selectedSeason = useMemo(
    () => filteredSeasons.find((s) => s.tournamentId === selectedTournamentId) ?? null,
    [filteredSeasons, selectedTournamentId],
  );

  const displayedGames = useMemo(() => {
    if (!selectedSeason) return categoryGames;
    return categoryGames.filter((g) => g.tournamentSlug === selectedSeason.slug);
  }, [categoryGames, selectedSeason]);

  function handleFilterClick(tournamentId: number) {
    if (selectedTournamentId === tournamentId) {
      // повторный клик сбрасывает фильтр
      setSelectedTournamentId(null);
    } else {
      setSelectedTournamentId(tournamentId);
    }
  }

  function handleResetFilter() {
    setSelectedTournamentId(null);
  }

  // Таблица турнира, открытая прямо на странице профиля (без перехода).
  const [tableSeason, setTableSeason] = useState<
    | null
    | { tournamentId: number; slug: string; name: string; season: string; ageCategory:string; isCup:boolean; cup?:CupData; table: TableRowLite[] }
  >(null);
  const [tableLoading, setTableLoading] = useState(false);
  const [tableError, setTableError] = useState<string | null>(null);

  async function openTable(season: { tournamentId: number; slug: string; name: string; season: string; ageCategory:string; isCup:boolean }) {
    if (tableSeason?.tournamentId === season.tournamentId) {
      // повторный клик — закрываем таблицу
      closeTable();
      return;
    }
    const request=++tableRequest.current;
    setTableSeason({ ...season, table: [] });
    setTableError(null);
    setTableLoading(true);
    try {
      const response = await fetch(`/api/tournaments/${season.slug}/${season.isCup?"cup":"table"}`);
      if (!response.ok) throw new Error();
      const payload = await response.json();
      if(request===tableRequest.current) setTableSeason({ ...season, table: payload.table ?? [], cup: payload.cup });
    } catch {
      if(request===tableRequest.current) setTableError(season.isCup?"Не удалось загрузить сетку кубка":"Не удалось загрузить таблицу турнира");
    } finally {
      if(request===tableRequest.current) setTableLoading(false);
    }
  }

  function closeTable() {
    tableRequest.current++;
    setTableSeason(null);
  }

  function changeFilters(setter:(value:string)=>void,value:string){setter(value);setSelectedTournamentId(null);closeTable();}
  const renderTable=()=>(tableSeason ? (
        <section
          id="club-table"
          aria-label={`${tableSeason.isCup?"Сетка кубка":"Таблица турнира"} ${tournamentTitle(tableSeason.name,tableSeason.ageCategory)}`}
          className="glass-card overflow-hidden rounded-2xl border-amber-300/25"
        >
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-emerald-400/15 px-5 py-4">
            <div>
              <p className="text-[11px] font-black uppercase tracking-[0.14em] text-amber-200">
                {tableSeason.season || "Сезон не указан"}
              </p>
              <h3 className="text-lg font-black uppercase tracking-wide text-white">
                {tableSeason.isCup?"🏆 Сетка кубка":"📊 Таблица турнира"} «{tournamentTitle(tableSeason.name,tableSeason.ageCategory)}»
              </h3>
              <p className="text-xs text-emerald-200/50">
                {tableSeason.isCup?"Сетка с соответствующей страницы кубка":"Турнирная таблица"} — показана под выбранным турниром
              </p>
            </div>
            <button
              type="button"
              onClick={closeTable}
              className="rounded-full border border-amber-300/35 px-3 py-1.5 text-xs font-bold text-amber-200 transition hover:bg-amber-400/10 active:scale-95"
            >
              Закрыть ✕
            </button>
          </div>

          {tableLoading ? (
            <div className="flex items-center justify-center gap-3 px-5 py-10 text-sm text-emerald-200/70">
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-emerald-300 border-t-transparent" />
              {tableSeason.isCup?"Загружаем сетку…":"Загружаем таблицу…"}
            </div>
          ) : tableError ? (
            <p className="px-5 py-8 text-center text-sm text-rose-300">{tableError}</p>
          ) : tableSeason.isCup ? (tableSeason.cup ? <div className="p-4"><CupBracket cup={tableSeason.cup}/></div> : null) : (
            <div className="scrollbar-thin overflow-x-auto">
              <table className="tabular w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="bg-emerald-950/60 text-[11px] uppercase tracking-wider text-emerald-200/55">
                    {["Место", "Команда", "И", "В", "Н", "П", "Мячи", "±", "О"].map(
                      (label, index) => (
                        <th
                          key={label}
                          className={`px-3 py-2.5 ${index === 1 ? "text-left" : "text-center"}`}
                        >
                          {label}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {tableSeason.table.map((row) => {
                    const isCurrent = row.team === club.name;
                    return (
                      <tr
                        key={row.teamId}
                        className={`border-b border-emerald-400/5 transition ${
                          isCurrent
                            ? "bg-amber-300/15 ring-1 ring-inset ring-amber-300/40"
                            : "hover:bg-emerald-400/5"
                        }`}
                      >
                        <td className="px-3 py-2 text-center">
                          <span className="inline-flex items-center gap-1 font-black text-white">
                            {row.position <= 3 ? ["🥇", "🥈", "🥉"][row.position - 1] : null}
                            {row.position}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={`font-bold ${isCurrent ? "text-amber-200" : "text-white"}`}
                          >
                            {isCurrent ? "★ " : ""}
                            {row.team}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center text-emerald-100/75">{row.played}</td>
                        <td className="px-3 py-2 text-center text-emerald-300">{row.won}</td>
                        <td className="px-3 py-2 text-center text-amber-200">{row.drawn}</td>
                        <td className="px-3 py-2 text-center text-rose-200">{row.lost}</td>
                        <td className="px-3 py-2 text-center">
                          {row.goalsFor}–{row.goalsAgainst}
                        </td>
                        <td
                          className={`px-3 py-2 text-center font-semibold ${
                            row.goalDiff > 0
                              ? "text-emerald-300"
                              : row.goalDiff < 0
                                ? "text-rose-300"
                                : "text-emerald-100/60"
                          }`}
                        >
                          {row.goalDiff > 0 ? `+${row.goalDiff}` : row.goalDiff}
                        </td>
                        <td className="px-3 py-2 text-center text-base font-black text-white">
                          {row.points}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null);
  const renderMatches=()=>(<section
        id="club-matches"
        className="glass-card scroll-mt-24 overflow-hidden rounded-2xl"
      >
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-emerald-400/15 px-5 py-4">
          <div>
            <h3 className="text-lg font-black uppercase tracking-wide text-white">
              {selectedSeason ? (
                <>
                  Матчи турнира «{tournamentTitle(selectedSeason.name,selectedSeason.ageCategory)}»{" "}
                  <span className="text-emerald-300">({displayedGames.length})</span>
                </>
              ) : (
                <>
                  Матчи во всех турнирах{" "}
                  <span className="text-emerald-300">({displayedGames.length})</span>
                </>
              )}
            </h3>
            {selectedSeason ? (
              <p className="text-xs text-emerald-200/60">
                Показаны только матчи турнира {selectedSeason.season || ""} «{tournamentTitle(selectedSeason.name,selectedSeason.ageCategory)}»
              </p>
            ) : (
              <p className="text-xs text-emerald-200/50">
                Полная история игр клуба во всех сезонах
              </p>
            )}
          </div>
          {selectedSeason ? (
            <button
              type="button"
              onClick={handleResetFilter}
              className="rounded-full bg-emerald-400/15 px-4 py-1.5 text-xs font-bold text-emerald-200 ring-1 ring-emerald-400/30 transition hover:bg-emerald-400/25 active:scale-95"
            >
              Закрыть матчи
            </button>
          ) : null}
        </div>
        <ul className="divide-y divide-emerald-400/5">
          {displayedGames.map((game) => (
            <li key={game.id} className="px-4 py-3 sm:px-5">
              <Link
                href={`/tournaments/${game.tournamentSlug}#match-${game.id}`}
                className="group block"
              >
                <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 text-sm">
                  <span className="truncate text-right font-semibold text-white group-hover:text-emerald-200">
                    {game.home}
                  </span>
                  <span
                    className={`tabular rounded-lg px-3 py-1 font-black ring-1 ${
                      game.result === "win"
                        ? "bg-emerald-400/15 text-emerald-300 ring-emerald-400/25"
                        : game.result === "loss"
                          ? "bg-rose-400/10 text-rose-200 ring-rose-400/20"
                          : "bg-amber-400/10 text-amber-200 ring-amber-400/20"
                    }`}
                  >
                    {game.homeGoals} : {game.awayGoals}
                  </span>
                  <span className="truncate font-semibold text-white group-hover:text-emerald-200">
                    {game.away}
                  </span>
                </div>
                <p className="mt-1 text-center text-[11px] text-emerald-200/45">
                  {[
                    tournamentTitle(game.tournamentName,game.ageCategory),
                    game.round ? `${game.round}-й тур` : null,
                    game.detailsLabel,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </Link>
            </li>
          ))}
        </ul>
        {!displayedGames.length ? (
          <p className="px-5 py-6 text-center text-sm text-emerald-200/50">
            {selectedSeason
              ? "В этом турнире у команды пока нет матчей."
              : "Матчей пока нет."}
          </p>
        ) : null}
      </section>);

  return (
    <div className="space-y-6">
      <section className="glass-card rounded-2xl p-5 sm:p-7">
        <div className="flex flex-wrap items-center justify-between gap-5">
          <div className="flex items-center gap-4">
            <TeamCrest
              name={club.name}
              short={club.short}
              colors={club.colors}
              logo={club.logo}
              pattern={club.kitPattern}
              size={72}
            />
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-emerald-300">
                Общий профиль клуба
              </p>
              <h2 className="mt-1 text-2xl font-black text-white sm:text-3xl">{club.name}</h2>
              {club.city ? <p className="text-sm text-emerald-200/55">{club.city}</p> : null}
              <p className="mt-1 text-xs text-emerald-200/50">
                {filteredSeasons.length} турниров · {ageCategory === "all" ? "все возрастные категории" : `категория ${ageCategory}`}
              </p>
            </div>
          </div>
          <TeamKit name={club.name} colors={club.colors} pattern={club.kitPattern} size={84} />
        </div>

        <dl className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
          {[
            ["Матчей", totals.played],
            ["Победы", totals.won],
            ["Ничьи", totals.drawn],
            ["Поражения", totals.lost],
            ["Мячи", `${totals.goalsFor}–${totals.goalsAgainst}`],
            ["Очки", totals.points],
          ].map(([label, value]) => (
            <div
              key={label}
              className="rounded-xl bg-emerald-950/60 px-4 py-3 text-center ring-1 ring-emerald-400/15"
            >
              <dt className="text-center text-[10px] font-bold uppercase tracking-wider text-emerald-200/50">
                {label}
              </dt>
              <dd className="tabular text-center text-2xl font-black text-white">{value}</dd>
            </div>
          ))}
        </dl>
        <p className="mt-3 text-xs text-emerald-200/45">
          Общая статистика рассчитана по внесённым матчам, каждый матч учитывается один раз.
          Изменение результата в админке обновляет оба клуба и их сводные показатели.
        </p>
      </section>

        <div><TournamentFilters fields={[
          {label:"Возраст",value:ageCategory,options:[...new Set(seasons.map(s=>s.ageCategory))].sort(),onChange:v=>changeFilters(setAgeCategory,v)},
          {label:"Год",value:year,options:[...new Set(seasons.flatMap(s=>tournamentYears(s.season)))].sort((a,b)=>Number(b)-Number(a)),onChange:v=>changeFilters(setYear,v)},
          {label:"Тип турнира",value:type,options:[...new Set(seasons.map(s=>tournamentType(s.name)))].sort(),onChange:v=>changeFilters(setType,v)},
        ]} onReset={()=>{setAgeCategory("all");setYear("all");setType("all");setSelectedTournamentId(null);closeTable();}} /></div>

      <section className="glass-card overflow-hidden rounded-2xl">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-emerald-400/15 px-5 py-4">
          <div>
            <h3 className="text-lg font-black uppercase tracking-wide text-white">По турнирам</h3>
            <p className="text-xs text-emerald-200/50">
              Таблица, кубковая сетка и матчи команды раскрываются сразу под выбранным турниром.
            </p>
          </div>
          {selectedSeason ? (
            <button
              type="button"
              onClick={handleResetFilter}
              className="rounded-full border border-emerald-300/40 px-3 py-1.5 text-xs font-bold text-emerald-200 transition hover:bg-emerald-400/10 active:scale-95"
            >
              Сбросить фильтр матчей ✕
            </button>
          ) : null}
        </div>
        <div className="scrollbar-thin overflow-x-auto">
          <table className="tabular w-full min-w-[720px] text-sm">
            <thead>
              <tr className="bg-emerald-950/60 text-[11px] uppercase tracking-wider text-emerald-200/55">
                {["Турнир / сезон", "И", "В", "Н", "П", "Мячи", "О", "Место"].map(
                  (label, index) => (
                    <th
                      key={label}
                      className={`px-3 py-2.5 ${index ? "text-center" : "text-left"}`}
                    >
                      {label}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {filteredSeasons.map((season) => {
                const isSelected = selectedTournamentId === season.tournamentId;
                return (
                  <Fragment key={season.teamId}><tr
                    className={`border-b border-emerald-400/5 transition ${
                      isSelected ? "bg-emerald-400/15" : "hover:bg-emerald-400/5"
                    }`}
                  >
                    <td className="px-3 py-3">
                      <Link href={`/tournaments/${season.slug}`} className="block whitespace-nowrap font-bold text-white hover:text-emerald-300">
                        <span className="text-emerald-300">{season.season || "Сезон не указан"}</span> · {tournamentTitle(season.name,season.ageCategory)}
                      </Link>
                      <span className="mt-2 flex flex-wrap gap-1.5">
                        <button
                          type="button"
                          onClick={() => handleFilterClick(season.tournamentId)}
                          aria-pressed={isSelected}
                          className={`rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-wide transition active:scale-95 ${
                            isSelected
                              ? "bg-emerald-400 text-emerald-950 shadow-[0_0_14px_rgba(52,211,153,0.6)]"
                              : "bg-emerald-400/10 text-emerald-200 ring-1 ring-emerald-400/25 hover:bg-emerald-400/25"
                          }`}
                          title={`Показать под этим турниром матчи команды «${season.teamName}» в турнире «${tournamentTitle(season.name,season.ageCategory)}»`}
                        >
                          {isSelected ? "✓ Матчи команды" : "📋 Матчи команды"}
                        </button>
                        <button
                          type="button"
                          onClick={() => openTable(season)}
                          aria-pressed={tableSeason?.tournamentId === season.tournamentId}
                          className={`rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-wide transition active:scale-95 ${
                            tableSeason?.tournamentId === season.tournamentId
                              ? "bg-amber-300 text-emerald-950 shadow-[0_0_14px_rgba(252,211,77,0.5)]"
                              : "border border-amber-300/35 text-amber-200 hover:bg-amber-400/10"
                          }`}
                          title={`Показать под этим турниром ${season.isCup?"сетку":"таблицу"} «${tournamentTitle(season.name,season.ageCategory)}»`}
                        >
                          {tableSeason?.tournamentId === season.tournamentId
                            ? (season.isCup?"✓ Сетка":"✓ Таблица")
                            : (season.isCup?"🏆 Сетка":"📊 Таблица")}
                        </button>
                        {admin ? (
                          <Link
                            href={`/admin/teams/${season.teamId}?tab=matches`}
                            className="rounded-full border border-amber-300/40 px-3 py-1 text-[11px] font-bold text-amber-200 transition hover:bg-amber-400/10"
                          >
                            ✏️ Редактировать
                          </Link>
                        ) : null}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center">{season.stats.played}</td>
                    <td className="px-3 py-3 text-center text-emerald-300">{season.stats.won}</td>
                    <td className="px-3 py-3 text-center text-amber-200">{season.stats.drawn}</td>
                    <td className="px-3 py-3 text-center text-rose-200">{season.stats.lost}</td>
                    <td className="px-3 py-3 text-center">
                      {season.stats.goalsFor}–{season.stats.goalsAgainst}
                    </td>
                    <td className="px-3 py-3 text-center font-black">
                      {season.stats.points}

                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className="inline-flex items-center gap-1 font-black text-white">
                        {season.position && season.position <= 3
                          ? ["🥇", "🥈", "🥉"][season.position - 1]
                          : null}
                        {season.position ?? "—"}
                      </span>
                    </td>
                  </tr>
                  {tableSeason?.tournamentId===season.tournamentId ? <tr><td colSpan={8} className="p-3">{renderTable()}</td></tr> : null}
                  {isSelected ? <tr><td colSpan={8} className="p-3">{renderMatches()}</td></tr> : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {!filteredSeasons.length ? (
          <p className="px-5 py-6 text-center text-sm text-emerald-200/50">
            По выбранным фильтрам турниров нет.
          </p>
        ) : null}
      </section>


      {!selectedSeason ? renderMatches() : null}

    </div>
  );
}
